'use client';
import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
}

let toastQueue: ToastMessage[] = [];
let toastListeners: ((toasts: ToastMessage[]) => void)[] = [];

export function showToast(message: string, type: ToastType = 'success') {
  const id = `toast-${Date.now()}`;
  toastQueue = [...toastQueue, { id, type, message }];
  toastListeners.forEach((l) => l([...toastQueue]));
  setTimeout(() => {
    toastQueue = toastQueue.filter((t) => t.id !== id);
    toastListeners.forEach((l) => l([...toastQueue]));
  }, 3500);
}

export default function ToastContainer() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  useEffect(() => {
    const listener = (updated: ToastMessage[]) => setToasts(updated);
    toastListeners.push(listener);
    return () => {
      toastListeners = toastListeners.filter((l) => l !== listener);
    };
  }, []);

  return (
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className="pointer-events-auto flex items-center gap-3 bg-card border border-border rounded-xl shadow-lg px-4 py-3 min-w-[280px] max-w-sm animate-in slide-in-from-bottom-4 fade-in-0 duration-200"
        >
          {toast.type === 'success' && <CheckCircle size={18} className="text-primary flex-shrink-0" />}
          {toast.type === 'error' && <XCircle size={18} className="text-red-500 flex-shrink-0" />}
          {toast.type === 'info' && <Info size={18} className="text-blue-500 flex-shrink-0" />}
          <span className="text-sm text-foreground font-medium flex-1">{toast.message}</span>
          <button
            onClick={() => {
              toastQueue = toastQueue.filter((t) => t.id !== toast.id);
              toastListeners.forEach((l) => l([...toastQueue]));
            }}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Dismiss notification"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}